import React, { useState, useMemo, useCallback } from "react";
import ContextMenu from "../../../common/ContextMenu";
import AdminBalanceModal from "./AdminBalanceModal";
import {
  DateCell,
  MemberGradeCellRender,
  ValueCell,
} from "../../../common/CustomCell";
import { getBankName } from "../../../common/data";
import { DataTable } from "../../../ui/data-table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
} from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Checkbox } from "../../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Plus, FileSpreadsheet, Search } from "lucide-react";

const PAGE_SIZE = 30;

const inquiryTypes = [
  { value: "0", label: "아이디" },
  { value: "1", label: "이름" },
  { value: "2", label: "핸드폰" },
  { value: "3", label: "이메일" },
  { value: "4", label: "추천인" },
  { value: "5", label: "아이피" },
  { value: "6", label: "맥주소" },
];

const memberListColumns = (handleOnClick) => [
  {
    accessorKey: "id",
    header: "아이디",
    enableSorting: true,
    size: 120,
  },
  {
    id: "basicInfo",
    header: "기본정보",
    columns: [
      {
        accessorKey: "createdAt",
        header: "가입일시",
        size: 160,
        enableSorting: true,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "userName",
        header: "이름",
        size: 120,
      },
      {
        accessorKey: "userPw",
        header: "패스워드",
        size: 80,
      },
      {
        accessorKey: "phoneNo",
        header: "휴대폰",
        size: 120,
      },
      {
        accessorKey: "email",
        header: "이메일",
        size: 160,
      },
      {
        accessorKey: "birthDate",
        header: "생년월일",
        cell: ({ getValue }) => getValue() || "",
        size: 110,
      },
      {
        accessorKey: "recommendCode",
        header: "추천인",
        size: 96,
      },
      {
        accessorKey: "connectState",
        header: "접속상태",
        size: 80,
        cell: ({ getValue }) => <span>{getValue() && "접속중"}</span>,
      },
      {
        accessorKey: "lastLogin",
        header: "최종접속일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "lastTrade",
        header: "마지막거래일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "connectIP",
        header: "접속IP",
        size: 160,
      },
      {
        accessorKey: "connectMAC",
        header: "접속MAC",
        size: 160,
      },
      {
        accessorKey: "authority",
        header: "권한",
        size: 120,
        cell: ({ getValue }) => <MemberGradeCellRender value={getValue()} />,
      },
      {
        accessorKey: "userState",
        header: "계정상태",
        size: 96,
        cell: ({ getValue }) => (
          <span>{getValue() === 1 ? "정상" : "블랙"}</span>
        ),
      },
      {
        accessorKey: "userBalance",
        header: "담보금",
        size: 120,
        enableSorting: true,
        cell: ({ getValue }) => <ValueCell value={getValue()} type={0} />,
      },
    ],
  },
  {
    id: "depositBank",
    header: "입금은행",
    columns: [
      {
        accessorKey: "deposit_accountName",
        header: "입금은행",
        size: 120,
      },
      {
        accessorKey: "deposit_accountBank",
        header: "입금은행명",
        size: 120,
        cell: ({ getValue }) => <span>{getBankName(getValue())}</span>,
      },
      {
        accessorKey: "deposit_accountOwner",
        header: "입금은행 계좌주",
        size: 160,
      },
      {
        accessorKey: "deposit_accountNo",
        header: "입금은행 계좌번호",
        size: 160,
      },
    ],
  },
  {
    id: "withdrawal",
    header: "출금",
    columns: [
      {
        accessorKey: "userAccountBank",
        header: "출금은행",
        size: 120,
        cell: ({ getValue }) => <span>{getBankName(getValue())}</span>,
      },
      {
        accessorKey: "userAccountNo",
        header: "출금계좌",
        size: 160,
      },
      {
        accessorKey: "userAccountOwner",
        header: "출금예금주",
        size: 120,
      },
    ],
  },
  {
    id: "overnight",
    header: "오버나잇",
    columns: [
      {
        accessorKey: "isOverDomestic",
        header: "국선오버나잇",
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          return <Badge variant={value ? "success" : "destructive"}>{value ? "설정" : "해제"}</Badge>;
        },
      },
      {
        accessorKey: "updatedData_overDomestic",
        header: "국선오버나잇 일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "isOverOption",
        header: "옵션오버나잇",
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          return <Badge variant={value ? "success" : "destructive"}>{value ? "설정" : "해제"}</Badge>;
        },
      },
      {
        accessorKey: "updatedData_overOption",
        header: "옵션오버나잇 일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "isOverForeign",
        header: "해선오버나잇",
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          return <Badge variant={value ? "success" : "destructive"}>{value ? "설정" : "해제"}</Badge>;
        },
      },
      {
        accessorKey: "updatedData_overForeign",
        header: "해선오버나잇 일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "isOverHangSeng",
        header: "항셍오버나잇",
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          return <Badge variant={value ? "success" : "destructive"}>{value ? "설정" : "해제"}</Badge>;
        },
      },
      {
        accessorKey: "updatedData_overHangseng",
        header: "항셍오버나잇 일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
      {
        accessorKey: "isOverMiniForeign",
        header: "마이크로",
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          return <Badge variant={value ? "success" : "destructive"}>{value ? "설정" : "해제"}</Badge>;
        },
      },
      {
        accessorKey: "updatedData_miniForeign",
        header: "마이크로 일시",
        size: 160,
        cell: ({ getValue }) => <DateCell value={getValue()} type={0} />,
      },
    ],
  },
  {
    accessorKey: "isCustomFeeSettings",
    header: "수수료설정",
    size: 96,
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Badge variant={value ? "destructive" : "secondary"}>
          {value ? "개별" : "프리셋"}
        </Badge>
      );
    },
  },
  {
    id: "fees",
    header: "수수료",
    columns: [
      {
        accessorKey: "domestic_fee",
        header: "국내선물",
        size: 96,
      },
      {
        accessorKey: "option_fee",
        header: "옵션",
        size: 96,
      },
      {
        accessorKey: "foreign_fee",
        header: "해외선물",
        size: 96,
      },
      {
        accessorKey: "hangseng_fee",
        header: "항셍",
        size: 96,
      },
    ],
  },
  {
    id: "maxContracts",
    header: "최대계약수",
    columns: [
      {
        accessorKey: "domestic_maxAvailableCount",
        header: "국내선물",
        size: 96,
      },
      {
        accessorKey: "option_maxAvailableCount",
        header: "옵션",
        size: 96,
      },
      {
        accessorKey: "optionSellCount",
        header: "옵션매도",
        size: 96,
      },
      {
        accessorKey: "foreign_maxAvailableCount",
        header: "해외선물",
        size: 96,
      },
      {
        accessorKey: "hangseng_maxAvailableCount",
        header: "항셍",
        size: 96,
      },
      {
        accessorKey: "mini_foreign_maxAvailableCount",
        header: "마이크로해외선물",
        size: 136,
      },
    ],
  },
  {
    accessorKey: "memo",
    header: "메모",
    size: 160,
  },
  {
    id: "actions",
    header: "기능",
    size: 80,
    meta: {
      cellClassName: "sticky right-0 z-10 bg-white shadow-[-4px_0_8px_rgba(0,0,0,0.08)]",
      headerClassName: "sticky right-0 z-30 bg-background shadow-[-4px_0_8px_rgba(0,0,0,0.08)]",
    },
    cell: ({ row }) => (
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0"
        onClick={() => handleOnClick(row.original)}
      >
        수정
      </Button>
    ),
  },
];

export const MemberList = (props) => {
  const {
    dataSource,
    totalCount,
    handleOnClick,
    onAddNewMember,
    showTesterOnly,
    onShowTesterOnlyChange,
    excludeTester,
    onExcludeTesterChange,
    onInquiry,
    onExport,
    managerId,
    reviseUserBalance,
  } = props;

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [contextUser, setContextUser] = useState({});
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [balanceTargetId, setBalanceTargetId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [inquiryType, setInquiryType] = useState("0");
  const [inquiryValue, setInquiryValue] = useState("");
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);

  const columns = useMemo(() => memberListColumns(handleOnClick), [handleOnClick]);

  // 전체 데이터를 먼저 정렬한 뒤 slice (정렬 → 자르기 순서 보장)
  const sortedData = useMemo(() => {
    if (!sorting.length) return dataSource;
    const { id: sortKey, desc } = sorting[0];
    return [...dataSource].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return desc ? 1 : -1;
      if (aVal > bVal) return desc ? -1 : 1;
      return 0;
    });
  }, [dataSource, sorting]);

  const displayData = useMemo(
    () => sortedData.slice(0, visibleCount),
    [sortedData, visibleCount]
  );
  const hasMore = visibleCount < sortedData.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  const handleSortingChange = useCallback((newSorting) => {
    setSorting(newSorting);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const handleRightClick = useCallback((record, event) => {
    event.preventDefault();
    setContextUser({
      id: record.id,
      userName: record.userName,
      userBalance: record.userBalance,
    });
    setPosition({ x: event.clientX, y: event.clientY });
    setOpen(true);
  }, []);

  // 모달 대상을 스냅샷이 아닌 dataSource에서 id로 실시간 파생한다.
  // → 입출금 성공 후 목록이 갱신되면 모달의 '현재 잔고'도 자동으로 최신값을 반영한다.
  const balanceTarget = useMemo(
    () => dataSource.find((user) => user.id === balanceTargetId) ?? null,
    [dataSource, balanceTargetId]
  );

  const handleOpenAdminBalance = useCallback(() => {
    setBalanceTargetId(contextUser.id);
    setBalanceModalOpen(true);
  }, [contextUser]);

  const handleInquirySubmit = (e) => {
    e.preventDefault();
    setVisibleCount(PAGE_SIZE);
    onInquiry(Number(inquiryType), inquiryValue);
  };

  return (
    <Card className="shadow-sm border-0 gap-0 h-full flex flex-col">
      <CardHeader className="pb-3 border-b border-solid border-gray-200">
        <CardTitle className="text-base flex items-center gap-2">
          회원목록
          <Badge variant="secondary">{totalCount}건</Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          회원 정보를 조회하고 관리합니다
        </CardDescription>
        <CardAction>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onAddNewMember}>
              <Plus className="size-4" />
              신규등록
            </Button>
            <Button
              size="sm"
              onClick={() => onExport(sortedData)}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <FileSpreadsheet className="size-4" />
              엑셀
            </Button>
          </div>
        </CardAction>
      </CardHeader>

      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <Checkbox
              checked={showTesterOnly}
              onCheckedChange={onShowTesterOnlyChange}
            />
            테스터만
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <Checkbox
              checked={excludeTester}
              onCheckedChange={onExcludeTesterChange}
            />
            테스터 제외
          </label>
        </div>

        <form onSubmit={handleInquirySubmit} className="flex items-center gap-1.5">
          <Select value={inquiryType} onValueChange={setInquiryType}>
            <SelectTrigger size="sm" className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {inquiryTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className="w-48 h-8"
            placeholder="검색어 입력"
            value={inquiryValue}
            onChange={(e) => setInquiryValue(e.target.value)}
          />
          <Button type="submit" size="sm">
            <Search className="size-4" />
            조회
          </Button>
        </form>
      </div>

      <CardContent className="flex-1 min-h-0 overflow-hidden px-0 pb-0">
        <DataTable
          columns={columns}
          data={displayData}
          getRowId={(row) => row.id}
          stickyHeader
          infiniteScroll
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onRowContextMenu={handleRightClick}
          initialSorting={[{ id: "createdAt", desc: true }]}
          onSortingChange={handleSortingChange}
        />
      </CardContent>

      <ContextMenu
        open={open}
        position={position}
        setOpen={setOpen}
        contextUser={contextUser}
        onAdminBalance={handleOpenAdminBalance}
      />

      <AdminBalanceModal
        open={balanceModalOpen}
        setOpen={setBalanceModalOpen}
        targetUser={balanceTarget}
        managerId={managerId}
        reviseUserBalance={reviseUserBalance}
      />
    </Card>
  );
};
